import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Save, Flame, ShoppingCart, ArrowLeftRight, Wrench, Database, RefreshCw, DollarSign } from 'lucide-react';
import { BRANDS, SIZES } from '../constants/lpg';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TODAY = new Date().toISOString().split('T')[0];

const sel = 'px-2.5 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white w-full';
const inp = 'px-2.5 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white w-full';

function emptyRow() { return { id: Date.now(), brand: '', size: '', filled: 0, mt: 0, rate_per_kg: 0 }; }
function emptyPurRow() { return { id: Date.now(), brand: '', size: '', qty: 0, rate_per_kg: 0 }; }

/* ─── Gas Purchase ─────────────────────────────────────────── */
export function GasPurchase() {
  const { authHeader, company } = useAuth();
  const godownNames = company?.godowns || [];

  const [form, setForm] = useState({ date: TODAY, challan_no: '', vendor_name: '', godown: '', notes: '' });
  const [rows, setRows] = useState([emptyPurRow()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addRow   = () => setRows(r => [...r, emptyPurRow()]);
  const delRow   = (id) => setRows(r => r.filter(x => x.id !== id));
  const setField = (id, k, v) => setRows(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const items = rows.map(r => ({
        brand: r.brand, size: r.size,
        qty: +r.qty,
        kg_per_cyl: parseFloat(r.size) || 0,
        rate_per_kg: +r.rate_per_kg,
      }));
      await axios.post(`${API}/purchases`, {
        challan_no:  form.challan_no,
        date:        form.date,
        vendor_name: form.vendor_name,
        godown:      form.godown,
        items,
        notes: form.notes,
      }, { headers: authHeader() });
      setMsg('Purchase saved! Stock updated.');
      setForm({ date: TODAY, challan_no: '', vendor_name: '', godown: '', notes: '' });
      setRows([emptyPurRow()]);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error saving purchase.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-4xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><Flame size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-5">Gas Purchase</h1>

      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="small-label block mb-1.5">Date</label>
              <input className={inp} type="date" value={form.date} onChange={e => setF('date', e.target.value)} required />
            </div>
            <div>
              <label className="small-label block mb-1.5">Challan No</label>
              <input className={inp} type="text" value={form.challan_no} onChange={e => setF('challan_no', e.target.value)} placeholder="e.g. P-2211" required />
            </div>
            <div>
              <label className="small-label block mb-1.5">Vendor Name</label>
              <input className={inp} type="text" value={form.vendor_name} onChange={e => setF('vendor_name', e.target.value)} placeholder="e.g. HP Gas Agency" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Godown</label>
              <select className={sel} value={form.godown} onChange={e => setF('godown', e.target.value)} required>
                <option value="">Select godown…</option>
                {godownNames.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div className="small-label text-gray-500">Cylinder Lines</div>
            <button type="button" onClick={addRow}
              className="text-[13px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              <Plus size={13} /> Add row
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100">
              {['Brand','Size','Qty','Rate/KG'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400 whitespace-nowrap">{h}</th>
              ))}
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-gray-50">
                  <td className="px-3 py-2 w-36">
                    <select className={sel} value={row.brand} onChange={e => setField(row.id, 'brand', e.target.value)}>
                      <option value="">—</option>
                      {BRANDS.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-32">
                    <select className={sel} value={row.size} onChange={e => setField(row.id, 'size', e.target.value)}>
                      <option value="">—</option>
                      {SIZES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-24">
                    <input className={inp} type="number" min="0" value={row.qty}
                      onChange={e => setField(row.id, 'qty', +e.target.value)} />
                  </td>
                  <td className="px-3 py-2 w-28">
                    <input className={inp} type="number" min="0" step="0.01" value={row.rate_per_kg || ''}
                      onChange={e => setField(row.id, 'rate_per_kg', e.target.value)} placeholder="₹/kg" />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => delRow(row.id)} disabled={rows.length === 1}
                      className="text-gray-300 hover:text-red-500 disabled:opacity-30 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
          <label className="small-label block mb-2">Notes</label>
          <textarea rows={3} className={inp + ' resize-none'} placeholder="Optional notes…"
            value={form.notes} onChange={e => setF('notes', e.target.value)} />
        </div>

        <div className="flex justify-end gap-3 items-center">
          {msg && <span className={`text-sm font-medium ${msg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Gas Sale ──────────────────────────────────────────────── */
export function GasSale() {
  const { authHeader, company } = useAuth();
  const godownNames = company?.godowns || [];

  const [form, setForm] = useState({
    date: TODAY, challan_no: '', customer_id: '', customer_name: '',
    customer_phone: '', godown: '', driver: '', vehicle: '', notes: '',
  });
  const [rows, setRows] = useState([emptyRow()]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  useEffect(() => {
    axios.get(`${API}/customers`, { headers: authHeader() })
      .then(r => setCustomers(r.data))
      .catch(console.error);
  }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addRow   = () => setRows(r => [...r, emptyRow()]);
  const delRow   = (id) => setRows(r => r.filter(x => x.id !== id));
  const setField = (id, k, v) => setRows(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));

  const onCustomerChange = (e) => {
    const cust = customers.find(c => c.id === e.target.value);
    setForm(f => ({
      ...f,
      customer_id:    cust?.id    || '',
      customer_name:  cust?.name  || '',
      customer_phone: cust?.phone || '',
    }));
    // Auto-fill rate from customer's current rate card
    if (cust?.current_rate > 0) {
      setRows(r => r.map(row => ({ ...row, rate_per_kg: cust.current_rate })));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const items = rows.map(r => ({
        brand: r.brand, size: r.size,
        filled: +r.filled, mt: +r.mt,
        rate_per_kg: +r.rate_per_kg,
      }));
      await axios.post(`${API}/sales`, {
        challan_no:     form.challan_no,
        date:           form.date,
        customer_id:    form.customer_id,
        customer_name:  form.customer_name,
        customer_phone: form.customer_phone,
        godown:         form.godown,
        driver:         form.driver,
        vehicle:        form.vehicle,
        items,
        notes: form.notes,
      }, { headers: authHeader() });
      setMsg('Sale saved! Stock updated.');
      setForm({ date: TODAY, challan_no: '', customer_id: '', customer_name: '', customer_phone: '', godown: '', driver: '', vehicle: '', notes: '' });
      setRows([emptyRow()]);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error saving sale.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-4xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><ShoppingCart size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-5">Gas Sale</h1>
      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="small-label block mb-1.5">Date</label>
              <input className={inp} type="date" value={form.date} onChange={e => setF('date', e.target.value)} required />
            </div>
            <div>
              <label className="small-label block mb-1.5">Challan No</label>
              <input className={inp} type="text" value={form.challan_no} onChange={e => setF('challan_no', e.target.value)} placeholder="e.g. 7862" required />
            </div>
            <div>
              <label className="small-label block mb-1.5">Customer</label>
              <select className={sel} value={form.customer_id} onChange={onCustomerChange}>
                <option value="">Select customer…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="small-label block mb-1.5">Godown</label>
              <select className={sel} value={form.godown} onChange={e => setF('godown', e.target.value)} required>
                <option value="">Select godown…</option>
                {godownNames.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="small-label block mb-1.5">Driver Name</label>
              <input className={inp} type="text" value={form.driver} onChange={e => setF('driver', e.target.value)} placeholder="Driver name" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Vehicle No</label>
              <input className={inp} type="text" value={form.vehicle} onChange={e => setF('vehicle', e.target.value)} placeholder="e.g. MH-04-AB-1234" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div className="small-label text-gray-500">Cylinder Lines</div>
            <button type="button" onClick={addRow} className="text-[13px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              <Plus size={13} /> Add row
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100">
              {['Brand','Size','Filled','MT','Rate/KG'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400">{h}</th>
              ))}
              <th />
            </tr></thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-gray-50">
                  <td className="px-3 py-2 w-36">
                    <select className={sel} value={row.brand} onChange={e => setField(row.id,'brand',e.target.value)}>
                      <option value="">—</option>{BRANDS.map(b=><option key={b}>{b}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-32">
                    <select className={sel} value={row.size} onChange={e => setField(row.id,'size',e.target.value)}>
                      <option value="">—</option>{SIZES.map(s=><option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-24">
                    <input className={inp} type="number" min="0" value={row.filled} onChange={e=>setField(row.id,'filled',+e.target.value)} />
                  </td>
                  <td className="px-3 py-2 w-24">
                    <input className={inp} type="number" min="0" value={row.mt} onChange={e=>setField(row.id,'mt',+e.target.value)} />
                  </td>
                  <td className="px-3 py-2 w-28">
                    <input className={inp} type="number" min="0" step="0.01" value={row.rate_per_kg || ''} onChange={e=>setField(row.id,'rate_per_kg',e.target.value)} placeholder="₹/kg" />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={()=>delRow(row.id)} disabled={rows.length===1} className="text-gray-300 hover:text-red-500 disabled:opacity-30">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
          <label className="small-label block mb-2">Notes</label>
          <textarea rows={3} className={inp + ' resize-none'} placeholder="Optional notes…"
            value={form.notes} onChange={e => setF('notes', e.target.value)} />
        </div>
        <div className="flex justify-end gap-3 items-center">
          {msg && <span className={`text-sm font-medium ${msg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Godown Transfer ──────────────────────────────────────── */
export function GodownTransfer() {
  const { authHeader, company } = useAuth();
  const godownNames = company?.godowns || [];

  const [form, setForm] = useState({ date: TODAY, challan_no: '', from_godown: '', to_godown: '', driver: '', vehicle: '', notes: '' });
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addRow   = () => setRows(r => [...r, emptyRow()]);
  const delRow   = (id) => setRows(r => r.filter(x => x.id !== id));
  const setField = (id, k, v) => setRows(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.from_godown === form.to_godown) { setMsg('Source and destination godowns must be different.'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API}/godown-transfers`, {
        date: form.date, challan_no: form.challan_no,
        from_godown: form.from_godown, to_godown: form.to_godown,
        driver: form.driver, vehicle: form.vehicle,
        items: rows.map(r => ({ brand: r.brand, size: r.size, qty: +r.filled })),
        notes: form.notes,
      }, { headers: authHeader() });
      setMsg('Transfer saved! Stock updated.');
      setForm({ date: TODAY, challan_no: '', from_godown: '', to_godown: '', driver: '', vehicle: '', notes: '' });
      setRows([emptyRow()]);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error saving transfer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-4xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><ArrowLeftRight size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-2">Godown to Godown Transfer</h1>
      <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-5">Move filled cylinders from one godown to another. Receiver sends MT back.</p>
      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className="small-label block mb-1.5">Date</label>
              <input className={inp} type="date" value={form.date} onChange={e => setF('date', e.target.value)} required />
            </div>
            <div><label className="small-label block mb-1.5">Challan No</label>
              <input className={inp} type="text" value={form.challan_no} onChange={e => setF('challan_no', e.target.value)} placeholder="e.g. AMB-102" />
            </div>
            <div><label className="small-label block mb-1.5">From Godown</label>
              <select className={sel} value={form.from_godown} onChange={e => setF('from_godown', e.target.value)} required>
                <option value="">Select…</option>{godownNames.map(g=><option key={g}>{g}</option>)}
              </select>
            </div>
            <div><label className="small-label block mb-1.5">To Godown</label>
              <select className={sel} value={form.to_godown} onChange={e => setF('to_godown', e.target.value)} required>
                <option value="">Select…</option>{godownNames.map(g=><option key={g}>{g}</option>)}
              </select>
            </div>
            <div><label className="small-label block mb-1.5">Driver Name</label>
              <input className={inp} type="text" value={form.driver} onChange={e => setF('driver', e.target.value)} placeholder="Driver name" />
            </div>
            <div><label className="small-label block mb-1.5">Vehicle No</label>
              <input className={inp} type="text" value={form.vehicle} onChange={e => setF('vehicle', e.target.value)} placeholder="e.g. MH-04-AB-1234" />
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div>
              <div className="small-label text-gray-500">Cylinder Lines</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Sender gives filled, receiver gives MT back</div>
            </div>
            <button type="button" onClick={addRow} className="text-[13px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              <Plus size={13} /> Add row
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100">
              {['Brand','Size','Filled','MT'].map(h=>(
                <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400">{h}</th>
              ))}
              <th />
            </tr></thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-gray-50">
                  <td className="px-3 py-2 w-40"><select className={sel} value={row.brand} onChange={e=>setField(row.id,'brand',e.target.value)}><option value="">—</option>{BRANDS.map(b=><option key={b}>{b}</option>)}</select></td>
                  <td className="px-3 py-2 w-32"><select className={sel} value={row.size} onChange={e=>setField(row.id,'size',e.target.value)}><option value="">—</option>{SIZES.map(s=><option key={s}>{s}</option>)}</select></td>
                  <td className="px-3 py-2 w-28"><input className={inp} type="number" min="0" value={row.filled} onChange={e=>setField(row.id,'filled',+e.target.value)} /></td>
                  <td className="px-3 py-2 w-28"><input className={inp} type="number" min="0" value={row.mt} onChange={e=>setField(row.id,'mt',+e.target.value)} /></td>
                  <td className="px-3 py-2"><button type="button" onClick={()=>delRow(row.id)} disabled={rows.length===1} className="text-gray-300 hover:text-red-500 disabled:opacity-30"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
          <label className="small-label block mb-2">Notes</label>
          <textarea rows={3} className={inp + ' resize-none'} placeholder="Optional notes…"
            value={form.notes} onChange={e => setF('notes', e.target.value)} />
        </div>
        <div className="flex justify-end gap-3 items-center">
          {msg && <span className={`text-sm font-medium ${msg.includes('Error') || msg.includes('must be') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Adjustment / Defective ──────────────────────────────── */
function emptyAdjRow() { return { id: Date.now() + Math.random(), brand: '', size: '', reason: 'defective', qty: 0 }; }

const REASON_LABELS = {
  defective:   'Defective (filled → defective)',
  adj_plus:    'Add Filled (+)',
  adj_minus:   'Remove Filled (−)',
  mt_return:   'MT Return (add empties)',
  mt_dispatch: 'MT Dispatch (send empties)',
};

export function AdjustmentEntry() {
  const { authHeader, company } = useAuth();
  const godownNames = company?.godowns || [];

  const [form, setForm] = useState({ date: TODAY, godown: '', notes: '' });
  const [rows, setRows] = useState([emptyAdjRow()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const setF   = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addRow = () => setRows(r => [...r, emptyAdjRow()]);
  const delRow = (id) => setRows(r => r.filter(x => x.id !== id));
  const setField = (id, k, v) => setRows(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API}/adjustments`, {
        date:   form.date,
        godown: form.godown,
        notes:  form.notes,
        items:  rows.map(r => ({ brand: r.brand, size: r.size, reason: r.reason, qty: +r.qty })),
      }, { headers: authHeader() });
      setMsg('Adjustment saved! Stock updated.');
      setForm({ date: TODAY, godown: '', notes: '' });
      setRows([emptyAdjRow()]);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error saving adjustment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-4xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><Wrench size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-5">Adjustment / Defective</h1>
      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="small-label block mb-1.5">Date</label>
              <input className={inp} type="date" value={form.date} onChange={e => setF('date', e.target.value)} required />
            </div>
            <div>
              <label className="small-label block mb-1.5">Godown</label>
              <select className={sel} value={form.godown} onChange={e => setF('godown', e.target.value)} required>
                <option value="">Select godown…</option>
                {godownNames.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div className="small-label text-gray-500">Adjustment Lines</div>
            <button type="button" onClick={addRow}
              className="text-[13px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              <Plus size={13} /> Add row
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100">
              {['Brand','Size','Type','Qty'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400">{h}</th>
              ))}
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-gray-50">
                  <td className="px-3 py-2 w-36">
                    <select className={sel} value={row.brand} onChange={e => setField(row.id,'brand',e.target.value)}>
                      <option value="">—</option>{BRANDS.map(b=><option key={b}>{b}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-32">
                    <select className={sel} value={row.size} onChange={e => setField(row.id,'size',e.target.value)}>
                      <option value="">—</option>{SIZES.map(s=><option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-52">
                    <select className={sel} value={row.reason} onChange={e => setField(row.id,'reason',e.target.value)}>
                      {Object.entries(REASON_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 w-24">
                    <input className={inp} type="number" min="0" value={row.qty}
                      onChange={e => setField(row.id,'qty',+e.target.value)} />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => delRow(row.id)} disabled={rows.length===1}
                      className="text-gray-300 hover:text-red-500 disabled:opacity-30 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
          <label className="small-label block mb-2">Notes / Reason</label>
          <textarea rows={3} className={inp + ' resize-none'} placeholder="Reason for adjustment…"
            value={form.notes} onChange={e => setF('notes', e.target.value)} />
        </div>

        <div className="flex justify-end gap-3 items-center">
          {msg && <span className={`text-sm font-medium ${msg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Payment Entry ──────────────────────────────────────────── */
export function PaymentEntry() {
  const { authHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    date: TODAY, payment_type: 'receive',
    party_id: '', party_name: '',
    mode: 'NEFT', amount: '', reference: '', note: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  useEffect(() => {
    axios.get(`${API}/customers`, { headers: authHeader() })
      .then(r => setCustomers(r.data))
      .catch(console.error);
  }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPartyChange = (e) => {
    const cust = customers.find(c => c.id === e.target.value);
    setForm(f => ({
      ...f,
      party_id:   cust?.id   || '',
      party_name: cust?.name || '',
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API}/payments`, {
        date:         form.date,
        party_name:   form.party_name,
        party_id:     form.party_id,
        payment_type: form.payment_type,
        mode:         form.mode,
        amount:       +form.amount,
        reference:    form.reference,
        note:         form.note,
      }, { headers: authHeader() });
      setMsg('Payment recorded!');
      setForm({ date: TODAY, payment_type: 'receive', party_id: '', party_name: '', mode: 'NEFT', amount: '', reference: '', note: '' });
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error saving payment.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="px-6 py-6 max-w-2xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><DollarSign size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-5">Payment Entry</h1>
      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="small-label block mb-1.5">Date</label>
              <input className={inp} type="date" value={form.date} onChange={e => setF('date', e.target.value)} required />
            </div>
            <div><label className="small-label block mb-1.5">Type</label>
              <select className={sel} value={form.payment_type} onChange={e => setF('payment_type', e.target.value)}>
                <option value="receive">Receive from customer</option>
                <option value="pay">Pay to vendor</option>
              </select>
            </div>
            <div><label className="small-label block mb-1.5">Party</label>
              <select className={sel} value={form.party_id} onChange={onPartyChange}>
                <option value="">Select party…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="small-label block mb-1.5">Mode</label>
              <select className={sel} value={form.mode} onChange={e => setF('mode', e.target.value)}>
                <option>NEFT</option><option>RTGS</option><option>Cash</option><option>Cheque</option><option>UPI</option>
              </select>
            </div>
            <div><label className="small-label block mb-1.5">Amount (₹)</label>
              <input className={inp} type="number" min="0" step="0.01" value={form.amount}
                onChange={e => setF('amount', e.target.value)} placeholder="0.00" required />
            </div>
            <div><label className="small-label block mb-1.5">Reference No</label>
              <input className={inp} type="text" value={form.reference} onChange={e => setF('reference', e.target.value)} placeholder="UTR / cheque no" />
            </div>
          </div>
          <div><label className="small-label block mb-1.5">Note</label>
            <textarea rows={2} className={inp + ' resize-none'} placeholder="Optional note…"
              value={form.note} onChange={e => setF('note', e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-3 items-center">
          {msg && <span className={`text-sm font-medium ${msg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Bulk Purchase (Bullet) ───────────────────────────────── */
export function BulkPurchase() {
  const [saved, setSaved] = useState(false);
  const handleSave = (e) => { e.preventDefault(); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  return (
    <div className="px-6 py-6 max-w-2xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><Database size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-5">Bulk Purchase (Bullet)</h1>
      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="small-label block mb-1.5">Date</label><input className={inp} type="date" defaultValue="2026-09-20" /></div>
            <div><label className="small-label block mb-1.5">Tank / Location</label><input className={inp} type="text" defaultValue="Pune Bullet" /></div>
            <div><label className="small-label block mb-1.5">Vendor</label><input className={inp} type="text" placeholder="Vendor name" /></div>
            <div><label className="small-label block mb-1.5">Quantity (KG)</label><input className={inp} type="number" min="0" step="0.01" placeholder="0.00" /></div>
            <div><label className="small-label block mb-1.5">Rate (₹/KG)</label><input className={inp} type="number" min="0" step="0.01" placeholder="0.00" /></div>
            <div><label className="small-label block mb-1.5">Challan No</label><input className={inp} type="text" placeholder="e.g. BP-001" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          {saved && <span className="text-green-600 text-sm font-medium self-center">Saved!</span>}
          <button type="submit" className="btn-primary flex items-center gap-2"><Save size={14} /> Save Entry</button>
        </div>
      </form>
    </div>
  );
}

/* ─── Refill from Bullet ──────────────────────────────────── */
export function RefillBullet() {
  const { company } = useAuth();
  const godownNames = company?.godowns || [];

  const [rows, setRows] = useState([emptyRow()]);
  const [saved, setSaved] = useState(false);

  const addRow   = () => setRows(r => [...r, emptyRow()]);
  const delRow   = (id) => setRows(r => r.filter(x => x.id !== id));
  const setField = (id, k, v) => setRows(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));
  const handleSave = (e) => { e.preventDefault(); setSaved(true); setTimeout(() => setSaved(false), 2500); };

  return (
    <div className="px-6 py-6 max-w-3xl">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><RefreshCw size={11} /> Entry</div>
      <h1 className="font-black text-3xl tracking-tight mb-5">Refill from Bullet</h1>
      <form onSubmit={handleSave}>
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="small-label block mb-1.5">Date</label><input className={inp} type="date" defaultValue="2026-09-20" /></div>
            <div><label className="small-label block mb-1.5">Bullet Tank</label><input className={inp} type="text" defaultValue="Pune Bullet" /></div>
            <div><label className="small-label block mb-1.5">Target Godown</label><select className={sel}><option value="">Select godown…</option>{godownNames.map(g=><option key={g}>{g}</option>)}</select></div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div className="small-label text-gray-500">Cylinder Lines</div>
            <button type="button" onClick={addRow} className="text-[13px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"><Plus size={13} /> Add row</button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100">{['Brand','Size','Qty'].map(h=><th key={h} className="px-4 py-2.5 text-left small-label text-gray-400">{h}</th>)}<th /></tr></thead>
            <tbody>{rows.map(row=>(
              <tr key={row.id} className="border-b border-gray-50">
                <td className="px-3 py-2 w-40"><select className={sel} value={row.brand} onChange={e=>setField(row.id,'brand',e.target.value)}><option value="">—</option>{BRANDS.map(b=><option key={b}>{b}</option>)}</select></td>
                <td className="px-3 py-2 w-32"><select className={sel} value={row.size} onChange={e=>setField(row.id,'size',e.target.value)}><option value="">—</option>{SIZES.map(s=><option key={s}>{s}</option>)}</select></td>
                <td className="px-3 py-2 w-28"><input className={inp} type="number" min="0" value={row.filled} onChange={e=>setField(row.id,'filled',+e.target.value)} /></td>
                <td className="px-3 py-2"><button type="button" onClick={()=>delRow(row.id)} disabled={rows.length===1} className="text-gray-300 hover:text-red-500 disabled:opacity-30"><Trash2 size={14} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="flex justify-end gap-3">
          {saved && <span className="text-green-600 text-sm font-medium self-center">Saved!</span>}
          <button type="submit" className="btn-primary flex items-center gap-2"><Save size={14} /> Save Entry</button>
        </div>
      </form>
    </div>
  );
}
